import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { StatusIndicator } from '@neo/primitives/status-indicator'
import { Tooltip } from '@neo/primitives/tooltip'
import { ChevronDown, MoreVertical, Sparkles, User } from 'lucide-react'
import { useState } from 'react'
import type { FlowVersion } from '../../types'
import { formatChangeSummary, formatVersionTime, getVersionEditorLabel } from './version-utils'

interface FlowVersionRowProps {
  version: FlowVersion
  isSelected: boolean
  onViewVersion: () => void
  onSwitch: () => void
  onEditSummary: () => void
}

export const FlowVersionRow = ({
  version,
  isSelected,
  onViewVersion,
  onSwitch,
  onEditSummary,
}: FlowVersionRowProps) => {
  const editorLabel = getVersionEditorLabel(version)
  const summaryText = formatChangeSummary(version.change_summary)
  const description = version.change_description?.trim()
  const [isExpanded, setIsExpanded] = useState(false)

  // AI descriptions are one "- " bullet per changed node. Render as a real list so a
  // wrapped bullet hangs under its text, not under the marker. A manual note that isn't
  // bulleted falls back to plain text.
  const descLines = description
    ? description.split('\n').flatMap((l) => {
        const trimmed = l.trim()
        return trimmed ? [trimmed] : []
      })
    : []
  const bulletItems = descLines.every((l) => l.startsWith('-'))
    ? descLines.map((l) => l.replace(/^-\s*/, ''))
    : null

  // The row body (view trigger) and the right-hand controls (expand + kebab) are siblings
  // so neither <button> nests in the other. The chevron toggles the description (the
  // git-style body) and is shown only when one exists. The expanded description renders
  // BELOW the click row, outside the button: text stays selectable/copyable, a stray
  // click on it doesn't switch the viewed version, and a screen reader doesn't announce
  // the whole body as the button's label.
  return (
    <div
      className={cn(
        'group flex flex-col border-l-2 border-transparent px-4 py-3 hover:bg-muted',
        isSelected && 'border-primary bg-accent',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={onViewVersion}
          data-testid={`button-version-row-${version.id}`}
          className="flex flex-1 cursor-pointer flex-col gap-2 rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        >
          <div className="flex items-center gap-2">
            <span className="text-s-500 text-primary">v{version.version}</span>
            <span className="font-jetbrains text-xs-400 tabular-nums text-secondary">
              {formatVersionTime(version.created_at)}
            </span>
            {version.is_active && <StatusIndicator tone="success">Active</StatusIndicator>}
          </div>

          {summaryText && (
            <p
              className={cn('text-s-400 text-secondary', !isExpanded && 'line-clamp-1')}
              title={summaryText}
              data-testid={`version-summary-${version.id}`}
            >
              {summaryText}
            </p>
          )}

          <div
            className={cn(
              'flex items-center gap-1.5 text-s-400',
              version.is_vibe ? 'text-brand-text' : 'text-secondary',
            )}
          >
            {version.is_vibe ? (
              <Sparkles className="h-3.5 w-3.5" />
            ) : (
              <User className="h-3.5 w-3.5" />
            )}
            <span>{editorLabel}</span>
          </div>
        </button>

        <div className="flex items-center gap-0.5">
          {description && (
            <Tooltip content={isExpanded ? 'Hide description' : 'Show description'}>
              <Button
                variant="ghost"
                size="iconSm"
                aria-label={isExpanded ? 'Hide description' : 'Show description'}
                aria-expanded={isExpanded}
                data-testid={`button-version-expand-${version.id}`}
                onClick={(e) => {
                  e.stopPropagation()
                  setIsExpanded((v) => !v)
                }}
              >
                <ChevronDown
                  className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')}
                />
              </Button>
            </Tooltip>
          )}

          <DropdownMenu modal={false}>
            <Tooltip content="Version actions">
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="iconSm"
                  aria-label="Version actions"
                  data-testid={`button-version-menu-${version.id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={onEditSummary}
                data-testid={`button-version-edit-summary-${version.id}`}
              >
                Edit description
              </DropdownMenuItem>
              {!version.is_active && (
                <DropdownMenuItem
                  onClick={onSwitch}
                  data-testid={`button-version-switch-${version.id}`}
                >
                  Make this version active
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {isExpanded &&
        description &&
        (bulletItems ? (
          <ul
            className="mt-2 list-disc space-y-1 pl-4 text-s-400 text-secondary"
            data-testid={`version-description-${version.id}`}
          >
            {bulletItems.map((item, index) => (
              // AI bullets are one-per-node and can repeat verbatim (two nodes with the
              // same change text), so content alone isn't a unique key. The list is
              // rebuilt per render, never reordered — index-composite is stable enough.
              <li key={`${index}-${item}`}>{item}</li>
            ))}
          </ul>
        ) : (
          <p
            className="mt-2 whitespace-pre-line text-s-400 text-secondary"
            data-testid={`version-description-${version.id}`}
          >
            {description}
          </p>
        ))}
    </div>
  )
}
